// ============================================================================
// ReplanDialog — manually reorganize video due_dates (Iter30)
// Founder can shift any video's date via per-row date picker or quick actions.
// Single batch save → POST /api/projects/{pid}/replan
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { X, Loader2, Calendar, RotateCcw, Sparkles, GripVertical } from './icons';

export default function ReplanDialog({ project, videos, users, onClose, onSaved }) {
  // Local working copy : {task_id, title, due_date, assigned_to, video_idx}
  const [rows, setRows] = useState(
    [...videos]
      .sort((a, b) => (a.video_idx ?? 0) - (b.video_idx ?? 0))
      .map((v) => ({
        task_id: v.id, title: v.title,
        due_date: (v.due_date || '').slice(0, 10),
        assigned_to: v.assigned_to,
        video_format: v.video_format,
      }))
  );
  const [busy, setBusy] = useState(false);
  // Iter42 (post-deploy) — drag & drop reorder post-launch
  const [dragFromIdx, setDragFromIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  // Track whether the user actually reordered so we skip the reorder call when not needed
  const initialOrder = useMemo(() => rows.map((r) => r.task_id), []); // init snapshot

  const reorderRows = (from, to) => {
    if (from == null || to == null || from === to) return;
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  // Reset to cadence — re-distribute evenly between start_date and end_date
  const resetAuto = () => {
    if (!project.start_date || !project.end_date) {
      toast.error('Le projet n\'a pas de dates de début/fin');
      return;
    }
    const cadence = project.cadence_days || 1;
    const s = new Date(project.start_date);
    const end = new Date(project.end_date);
    const next = rows.map((r, i) => {
      const dt = new Date(s.getTime() + i * cadence * 86400000);
      const clamped = dt > end ? end : dt;
      return { ...r, due_date: clamped.toISOString().slice(0, 10) };
    });
    setRows(next);
    toast.success('Dates réinitialisées (cadence projet)');
  };

  const save = async () => {
    setBusy(true);
    try {
      // Iter42 (post-deploy) — if user drag-reordered, call reorder-videos FIRST.
      // The backend keeps dates aligned with slot position (V1 date stays in slot V1),
      // then our subsequent replan overrides due_dates if the user also edited them.
      const currentOrder = rows.map((r) => r.task_id);
      const reordered = currentOrder.some((id, i) => id !== initialOrder[i]);
      if (reordered) {
        await api.post(`/projects/${project.id}/reorder-videos`, { order: currentOrder });
      }
      const items = rows.map((r) => ({ task_id: r.task_id, due_date: r.due_date || null }));
      await api.post(`/projects/${project.id}/replan`, { items });
      const msg = reordered
        ? `${items.length} vidéo${items.length > 1 ? 's' : ''} réordonnée${items.length > 1 ? 's' : ''} et replanifiée${items.length > 1 ? 's' : ''}`
        : `${items.length} vidéo${items.length > 1 ? 's' : ''} replanifiée${items.length > 1 ? 's' : ''}`;
      toast.success(msg);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur replanification');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(10,26,51,0.70)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '24px 18px', overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{
        width: '100%', maxWidth: 640,
        background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 22, padding: 24,
        margin: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)' }}>
              <Calendar size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} />
              Réordonner &amp; replanifier les vidéos
            </h2>
            <div className="asa-eyebrow" style={{ marginTop: 4, color: 'var(--t3)' }}>
              {project.name} · {rows.length} vidéo{rows.length > 1 ? 's' : ''}
              {project.cadence_days ? ` · cadence ${project.cadence_days}j` : ''}
              {' '}· <span style={{ color: '#5BBEFC' }}>glisse pour réordonner</span>
            </div>
          </div>
          <button data-testid="replan-close" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>

        {/* Quick action */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button
            data-testid="replan-reset-auto"
            type="button" onClick={resetAuto}
            className="asa-btn-ghost asa-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
          >
            <RotateCcw size={12} /> Réinitialiser (cadence auto)
          </button>
        </div>

        {/* Rows */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 6,
          maxHeight: 480, overflowY: 'auto', paddingRight: 4,
        }}>
          {rows.map((r, i) => {
            const u = r.assigned_to ? userMap[r.assigned_to] : null;
            return (
              <div
                key={r.task_id}
                data-testid={`replan-row-${i}`}
                draggable
                onDragStart={(e) => {
                  setDragFromIdx(i);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(i));
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragOverIdx !== i) setDragOverIdx(i);
                }}
                onDragLeave={() => setDragOverIdx((prev) => (prev === i ? null : prev))}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = dragFromIdx ?? parseInt(e.dataTransfer.getData('text/plain'), 10);
                  reorderRows(from, i);
                  setDragFromIdx(null);
                  setDragOverIdx(null);
                }}
                onDragEnd={() => { setDragFromIdx(null); setDragOverIdx(null); }}
                style={{
                  display: 'grid', gridTemplateColumns: '20px 36px 1fr 160px 90px', gap: 10,
                  alignItems: 'center',
                  padding: '10px 12px', background: 'var(--sf2)',
                  border: dragOverIdx === i && dragFromIdx !== i
                    ? '1px dashed #5BBEFC'
                    : '1px solid var(--bd)',
                  borderRadius: 10,
                  opacity: dragFromIdx === i ? 0.4 : 1,
                  cursor: 'grab',
                  transition: 'opacity 0.12s, border-color 0.12s',
                }}
              >
                <div
                  data-testid={`replan-drag-handle-${i}`}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)' }}
                  title="Glisse pour réordonner"
                >
                  <GripVertical size={13} />
                </div>
                <div style={{
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: '#5BBEFC',
                  textAlign: 'center',
                }}>V{i + 1}</div>
                <div style={{
                  fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{r.title}</div>
                <input
                  data-testid={`replan-date-${i}`}
                  type="date"
                  value={r.due_date}
                  onChange={(e) => {
                    const next = [...rows];
                    next[i] = { ...next[i], due_date: e.target.value };
                    setRows(next);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  draggable={false}
                  style={{
                    fontSize: 12, padding: '6px 8px', colorScheme: 'dark',
                    fontFamily: 'Lexend',
                  }}
                />
                <div style={{
                  fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)',
                  textAlign: 'right',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {u?.name || '—'}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button
            data-testid="replan-cancel"
            type="button" onClick={onClose}
            className="asa-btn-ghost asa-btn" style={{ flex: 1 }}
          >Annuler</button>
          <button
            data-testid="replan-save"
            type="button" onClick={save} disabled={busy}
            style={{
              flex: 1.4,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '13px 22px', borderRadius: 12,
              background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 55%, #1E5FFF 100%)',
              border: '1.5px solid rgba(255,255,255,0.15)',
              color: '#FFFFFF',
              fontFamily: 'Sora', fontSize: 14, fontWeight: 800, letterSpacing: 0.2,
              cursor: busy ? 'wait' : 'pointer',
              boxShadow: '0 8px 24px rgba(30,95,255,0.55), 0 0 0 1px rgba(91,190,252,0.35) inset',
              transition: 'all 0.15s var(--ease-out)',
            }}
          >
            {busy ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
